import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Trophy, Loader2, Plus, Lock, Unlock, Dice5, Trash2, Crown, Ticket, Clock, Sparkles, X,
} from "lucide-react";
import { BannerDisplay } from "@/components/Banner";

function formatRemaining(endsAt) {
    if (!endsAt) return null;
    const ms = new Date(endsAt).getTime() - Date.now();
    if (ms <= 0) return { expired: true, label: "ENDED" };
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hrs = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    if (days > 0) return { expired: false, label: `${days}d ${hrs}h ${mins}m` };
    if (hrs > 0) return { expired: false, label: `${hrs}h ${mins}m` };
    return { expired: false, label: `${mins}m` };
}

function toLocalISO(dt) {
    // Convert a Date to ISO string in UTC
    return dt.toISOString();
}

function toDatetimeLocal(isoStr) {
    if (!isoStr) return "";
    const dt = new Date(isoStr);
    const tzOffsetMs = dt.getTimezoneOffset() * 60000;
    const localDt = new Date(dt.getTime() - tzOffsetMs);
    return localDt.toISOString().slice(0, 16);
}

export function GiveawayManager() {
    const [items, setItems] = useState([]);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [entriesDialog, setEntriesDialog] = useState(null); // giveaway being viewed
    const [entriesData, setEntriesData] = useState(null);
    const [winnerDialog, setWinnerDialog] = useState(null); // result of draw

    const [form, setForm] = useState({
        title: "",
        description: "",
        prize_type: "premium_weeks",
        prize_banner_id: "",
        prize_premium_weeks: 3,
        ends_at_local: "",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [g, b] = await Promise.all([
                api.get("/giveaways"),
                api.get("/shop/banners"),
            ]);
            setItems(g.data.items || []);
            setBanners(b.data.items || []);
        } catch (e) {
            toast.error("Failed to load giveaways");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submitCreate = async () => {
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        if (form.prize_type === "mythic_banner" && !form.prize_banner_id) {
            toast.error("Pick the banner you want to give away");
            return;
        }
        let endsAtIso = null;
        if (form.ends_at_local) {
            const dt = new Date(form.ends_at_local);
            if (Number.isNaN(dt.getTime())) {
                toast.error("Invalid end date/time");
                return;
            }
            endsAtIso = toLocalISO(dt);
        }
        setBusy("create");
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description.trim(),
                prize_type: form.prize_type,
                prize_banner_id: form.prize_type === "mythic_banner" ? form.prize_banner_id : null,
                prize_premium_weeks: form.prize_type === "premium_weeks" ? Number(form.prize_premium_weeks) || 3 : null,
                ends_at: endsAtIso,
            };
            await api.post("/admin/giveaways", payload);
            toast.success("Giveaway created \u{1F389}");
            setShowCreate(false);
            setForm({ ...form, title: "", description: "", ends_at_local: "" });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        } finally { setBusy(null); }
    };

    const lockGiveaway = async (g) => {
        if (!window.confirm(`Lock entries for "${g.title}"? After locking, no new entries can be bought.`)) return;
        setBusy(g.id);
        try {
            await api.post(`/admin/giveaways/${g.id}/lock`);
            toast.success("Locked — ready to draw");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(null); }
    };

    const unlockGiveaway = async (g) => {
        setBusy(g.id);
        try {
            await api.post(`/admin/giveaways/${g.id}/unlock`);
            toast.success("Reopened for entries");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(null); }
    };

    const drawWinner = async (g) => {
        if (!window.confirm(`PULL THE WINNER for "${g.title}"?\n\nThis randomly selects ONE winner from all paid entries and instantly grants the prize. This cannot be undone.`)) return;
        setBusy(g.id);
        try {
            const res = await api.post(`/admin/giveaways/${g.id}/draw`);
            setWinnerDialog({ ...res.data, title: g.title });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Draw failed");
        } finally { setBusy(null); }
    };

    const cancelGiveaway = async (g) => {
        if (!window.confirm(`Hide "${g.title}" from public listing? Existing entries are NOT auto-refunded — you'll need to refund through Stripe directly if needed.`)) return;
        setBusy(g.id);
        try {
            await api.delete(`/admin/giveaways/${g.id}`);
            toast.success("Giveaway hidden");
            await load();
        } catch (e) {
            toast.error("Failed");
        } finally { setBusy(null); }
    };

    const viewEntries = async (g) => {
        setEntriesDialog(g);
        setEntriesData(null);
        try {
            const res = await api.get(`/admin/giveaways/${g.id}/entries`);
            setEntriesData(res.data);
        } catch (e) {
            toast.error("Failed to load entries");
            setEntriesDialog(null);
        }
    };

    const mythicBanners = banners.filter((b) => b.rarity === "mythic" || b.rarity === "legendary");
    const otherBanners = banners.filter((b) => b.rarity !== "mythic" && b.rarity !== "legendary");

    return (
        <div className="space-y-6">
            {/* Header / Create */}
            <Card className="pp-bg-1 border-[#242436] text-white">
                <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: "#1f1a05", border: "1px solid #facc1555" }}>
                            <Trophy className="w-4 h-4 text-[#facc15]" />
                        </span>
                        <div>
                            <p className="font-display text-xl tracking-widest">GIVEAWAYS</p>
                            <p className="text-xs text-[#8b8ba0]">Drop mythic banners or free Premium weeks. Entries: $2 / 1 • $9 / 5 • $15 / 10.</p>
                        </div>
                    </div>
                    <Button onClick={() => setShowCreate(true)} className="bg-[#facc15] hover:brightness-110 text-[#1a1503] font-bold" data-testid="giveaway-create-button">
                        <Plus className="w-4 h-4 mr-1" /> New Giveaway
                    </Button>
                </CardContent>
            </Card>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2].map((i) => <Skeleton key={i} className="h-44 rounded-xl pp-bg-1" />)}
                </div>
            ) : items.length === 0 ? (
                <Card className="pp-bg-1 border-[#242436] text-white">
                    <CardContent className="p-8 text-center">
                        <p className="text-sm text-[#b7b7c6]">No giveaways yet — click "New Giveaway" to launch one.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((g) => {
                        const remaining = formatRemaining(g.ends_at);
                        const canDraw = g.status !== "drawn" && g.total_entries > 0;
                        const isLocked = g.status === "locked" || g.locked;
                        const isDrawn = g.status === "drawn";
                        return (
                            <Card key={g.id} className="pp-bg-1 border-[#242436] text-white" data-testid={`giveaway-admin-card-${g.id}`}>
                                <CardContent className="p-4 space-y-3">
                                    {/* Prize chip */}
                                    {g.prize_type === "mythic_banner" && g.prize_banner_image && (
                                        <BannerDisplay banner={{
                                            image: g.prize_banner_image,
                                            animation: g.prize_banner_animation || "pulse_purple",
                                            color: g.prize_banner_color || "#a855f7",
                                            name: g.prize_banner_name,
                                        }} height={70} />
                                    )}
                                    {g.prize_type === "premium_weeks" && (
                                        <div className="h-14 rounded-md flex items-center justify-center bg-gradient-to-r from-[#0f1f3a] to-[#1a0c2e] border border-[#facc15]55">
                                            <Crown className="w-4 h-4 text-[#facc15] mr-2" />
                                            <span className="font-display tracking-widest text-white text-sm">{g.prize_premium_weeks} WK PREMIUM</span>
                                        </div>
                                    )}
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="font-display text-base tracking-widest text-white truncate">{g.title}</p>
                                            <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px] font-mono uppercase">
                                                <span className="text-[#facc15]">
                                                    <Ticket className="inline w-3 h-3 mr-1" />{g.total_entries} entries
                                                </span>
                                                <span className="text-[#8b8ba0]">{g.unique_entrants} players</span>
                                                {remaining && !isDrawn && !isLocked && (
                                                    <span className="text-[#a855f7]">
                                                        <Clock className="inline w-3 h-3 mr-1" />{remaining.expired ? "ENDED" : remaining.label}
                                                    </span>
                                                )}
                                                {isDrawn && (
                                                    <Badge className="bg-[#07130b] text-[#22c55e] border border-[#22c55e]55">DRAWN</Badge>
                                                )}
                                                {isLocked && !isDrawn && (
                                                    <Badge className="bg-[#1f1a05] text-[#facc15] border border-[#facc15]55">LOCKED</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {isDrawn && g.winner_display_name && (
                                        <p className="text-xs font-mono text-[#22c55e]">
                                            <Sparkles className="inline w-3.5 h-3.5 mr-1" /> Winner: @{g.winner_display_name}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            onClick={() => viewEntries(g)}
                                            variant="secondary"
                                            className="flex-1 bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26] text-xs"
                                            data-testid={`giveaway-view-entries-${g.id}`}
                                        >
                                            <Ticket className="w-3.5 h-3.5 mr-1" /> Entries
                                        </Button>
                                        {!isDrawn && !isLocked && (
                                            <Button
                                                onClick={() => lockGiveaway(g)}
                                                disabled={busy === g.id}
                                                variant="secondary"
                                                className="bg-[#1f1a05] border border-[#facc15]55 text-[#facc15] hover:bg-[#2a2007] text-xs"
                                                data-testid={`giveaway-lock-${g.id}`}
                                            >
                                                {busy === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Lock className="w-3.5 h-3.5 mr-1" /> Lock</>}
                                            </Button>
                                        )}
                                        {!isDrawn && isLocked && (
                                            <Button
                                                onClick={() => unlockGiveaway(g)}
                                                disabled={busy === g.id}
                                                variant="secondary"
                                                className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26] text-xs"
                                                data-testid={`giveaway-unlock-${g.id}`}
                                            >
                                                {busy === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Unlock className="w-3.5 h-3.5 mr-1" /> Unlock</>}
                                            </Button>
                                        )}
                                        {!isDrawn && (
                                            <Button
                                                onClick={() => drawWinner(g)}
                                                disabled={busy === g.id || !canDraw}
                                                className="bg-[#22c55e] hover:brightness-110 text-[#07130b] font-bold text-xs"
                                                data-testid={`giveaway-draw-${g.id}`}
                                                title={canDraw ? "Pull random winner" : "No entries yet"}
                                            >
                                                {busy === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Dice5 className="w-3.5 h-3.5 mr-1" /> Draw</>}
                                            </Button>
                                        )}
                                        {!isDrawn && (
                                            <Button
                                                onClick={() => cancelGiveaway(g)}
                                                disabled={busy === g.id}
                                                variant="secondary"
                                                className="bg-[#1a0606] border border-[#ef4444]55 text-[#ef4444] hover:bg-[#2a0c0c] hover:text-white text-xs"
                                                data-testid={`giveaway-cancel-${g.id}`}
                                                title="Hide from public listing"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="pp-bg-1 border-[#242436] text-white max-w-lg" data-testid="giveaway-create-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-widest inline-flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-[#facc15]" /> NEW GIVEAWAY
                        </DialogTitle>
                        <DialogDescription className="text-[#b7b7c6]">Configure prize and timer. Entries open immediately after creation.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-[#b7b7c6]">Title *</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} placeholder="e.g. Friday Mythic Drop" className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="giveaway-create-title" />
                        </div>
                        <div>
                            <Label className="text-[#b7b7c6]">Description</Label>
                            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} placeholder="Hype it up. What’s the prize, why is it dope?" className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="giveaway-create-description" />
                        </div>
                        <div>
                            <Label className="text-[#b7b7c6]">Prize Type</Label>
                            <Select value={form.prize_type} onValueChange={(v) => setForm({ ...form, prize_type: v })}>
                                <SelectTrigger className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="giveaway-create-prize-type"><SelectValue /></SelectTrigger>
                                <SelectContent className="pp-bg-0 border-[#242436] text-white">
                                    <SelectItem value="premium_weeks" className="text-white">Free Sports Premium (weeks)</SelectItem>
                                    <SelectItem value="mythic_banner" className="text-white">Mythic / Legendary Banner</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {form.prize_type === "mythic_banner" && (
                            <div>
                                <Label className="text-[#b7b7c6]">Choose Banner</Label>
                                <Select value={form.prize_banner_id} onValueChange={(v) => setForm({ ...form, prize_banner_id: v })}>
                                    <SelectTrigger className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="giveaway-create-banner-select">
                                        <SelectValue placeholder="Select a banner..." />
                                    </SelectTrigger>
                                    <SelectContent className="pp-bg-0 border-[#242436] text-white max-h-72">
                                        {mythicBanners.length > 0 && <p className="px-2 py-1 text-[10px] uppercase font-mono text-[#facc15]">Mythic / Legendary</p>}
                                        {mythicBanners.map((b) => (
                                            <SelectItem key={b.id} value={b.id} className="text-white">{b.name} ({b.rarity})</SelectItem>
                                        ))}
                                        {otherBanners.length > 0 && <p className="px-2 py-1 text-[10px] uppercase font-mono text-[#8b8ba0] mt-1">Other</p>}
                                        {otherBanners.map((b) => (
                                            <SelectItem key={b.id} value={b.id} className="text-white">{b.name} ({b.rarity})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-[10px] text-[#8b8ba0] mt-1 font-mono">Tip: pre-create the banner in Banner Shop — active or retired both work.</p>
                            </div>
                        )}
                        {form.prize_type === "premium_weeks" && (
                            <div>
                                <Label className="text-[#b7b7c6]">Weeks of Premium</Label>
                                <Input type="number" min={1} max={52} value={form.prize_premium_weeks} onChange={(e) => setForm({ ...form, prize_premium_weeks: e.target.value })} className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="giveaway-create-weeks" />
                                <p className="text-[10px] text-[#8b8ba0] mt-1 font-mono">Default: 3 weeks</p>
                            </div>
                        )}
                        <div>
                            <Label className="text-[#b7b7c6]">Auto-lock at (optional)</Label>
                            <Input type="datetime-local" value={form.ends_at_local} onChange={(e) => setForm({ ...form, ends_at_local: e.target.value })} className="mt-1 pp-bg-0 border-[#242436] text-white" data-testid="giveaway-create-endsat" />
                            <p className="text-[10px] text-[#8b8ba0] mt-1 font-mono">Leave blank for manual control. After this time, no new entries can be bought.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setShowCreate(false)} disabled={busy === "create"} className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]">
                            Cancel
                        </Button>
                        <Button onClick={submitCreate} disabled={busy === "create"} className="bg-[#facc15] hover:brightness-110 text-[#1a1503] font-bold" data-testid="giveaway-create-submit">
                            {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><Trophy className="w-4 h-4" /> Launch Giveaway</span>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Entries Dialog */}
            <Dialog open={!!entriesDialog} onOpenChange={(open) => { if (!open) { setEntriesDialog(null); setEntriesData(null); } }}>
                <DialogContent className="pp-bg-1 border-[#242436] text-white max-w-lg" data-testid="giveaway-entries-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-widest inline-flex items-center gap-2">
                            <Ticket className="w-5 h-5 text-[#facc15]" /> ENTRIES — {entriesDialog?.title}
                        </DialogTitle>
                    </DialogHeader>
                    {!entriesData ? (
                        <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#facc15]" /></div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex justify-between text-xs font-mono uppercase">
                                <span className="text-[#facc15]">Total: {entriesData.total_entries}</span>
                                <span className="text-[#22c55e]">Players: {entriesData.unique_entrants}</span>
                            </div>
                            <div className="max-h-72 overflow-y-auto pp-bg-0 border border-[#242436] rounded-md">
                                {(entriesData.by_user || []).length === 0 ? (
                                    <p className="text-center text-sm text-[#8b8ba0] py-6">No entries yet</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 pp-bg-1 border-b border-[#242436]">
                                            <tr><th className="text-left py-2 px-3 font-mono uppercase text-[10px] text-[#8b8ba0]">User</th><th className="text-right py-2 px-3 font-mono uppercase text-[10px] text-[#8b8ba0]">Entries</th><th className="text-right py-2 px-3 font-mono uppercase text-[10px] text-[#8b8ba0]">Odds</th></tr>
                                        </thead>
                                        <tbody>
                                            {entriesData.by_user.map((u) => (
                                                <tr key={u.user_id} className="border-b border-[#242436] last:border-0">
                                                    <td className="py-2 px-3"><span className="text-white">@{u.display_name || u.email?.split("@")[0] || "player"}</span><span className="block text-[10px] text-[#8b8ba0] font-mono">{u.email}</span></td>
                                                    <td className="py-2 px-3 text-right text-[#facc15] font-mono">{u.entries}</td>
                                                    <td className="py-2 px-3 text-right text-[#22c55e] font-mono">{((u.entries / entriesData.total_entries) * 100).toFixed(1)}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => { setEntriesDialog(null); setEntriesData(null); }} className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]">
                            <X className="w-4 h-4 mr-1" /> Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Winner Dialog */}
            <Dialog open={!!winnerDialog} onOpenChange={(open) => { if (!open) setWinnerDialog(null); }}>
                <DialogContent className="pp-bg-1 border-[#242436] text-white" data-testid="giveaway-winner-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-widest inline-flex items-center gap-2 text-[#22c55e]">
                            <Sparkles className="w-5 h-5 text-[#22c55e]" /> WINNER PULLED!
                        </DialogTitle>
                        <DialogDescription className="text-[#b7b7c6]">{winnerDialog?.title}</DialogDescription>
                    </DialogHeader>
                    {winnerDialog && (
                        <div className="space-y-3 py-2">
                            <div className="text-center">
                                <Trophy className="w-12 h-12 mx-auto text-[#facc15] drop-shadow-[0_0_8px_rgba(250,204,21,0.6)]" />
                                <p className="font-display text-2xl tracking-widest text-white mt-2">@{winnerDialog.winner_display_name}</p>
                                <p className="text-xs font-mono text-[#8b8ba0]">{winnerDialog.winner_email}</p>
                            </div>
                            <div className="text-center text-sm text-[#22c55e] font-mono">
                                Awarded: {winnerDialog.prize_summary}
                            </div>
                            <div className="text-center text-[10px] text-[#8b8ba0] font-mono">
                                Drawn from {winnerDialog.total_entries_at_draw} entries • Live feed updated
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setWinnerDialog(null)} className="bg-[#22c55e] hover:brightness-110 text-[#07130b] font-bold w-full">
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
